import cn from 'classnames'
import React, { ComponentPropsWithRef, ReactNode, forwardRef, useState } from 'react'
import styled from 'styled-components'

import { CloseEyeIcon } from '../../lib/icons/close-eye-icon'
import { EyeIcon } from '../../lib/icons/eye-icon'
import { SShowPasswordToggle } from '../../lib/password-input'

import InputWrapper from './InputWrapper'

const SInput = styled.input`
    border: unset;
    padding: 10px 15px;
    font-size: 16px;
    font-weight: 300;
    width: 100%;
    position: relative;
    cursor: inherit;
    background-color: ${({ theme }) => theme.font.backgroundColor};
    max-height: 40px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: 600px) {
        font-size: 14px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        & ~ label {
            font-size: 12px;
        }
    }
    &.password {
        padding-right: 40px;
    }

    &:not(:placeholder-shown),
    &:focus {
        & ~ label {
            font-size: 12px;
            top: -10px;
            background-color: white;
            border-radius: 2px;
        }
    }

    &:disabled {
        border-color: ${({ theme }) => theme.brandColors.CA_Light_Gray};
        color: ${({ theme }) => theme.brandColors.CA_Dark_Gray};

        & ~ label {
            color: ${({ theme }) => theme.brandColors.CA_Light_Gray};
        }
    }

    &.read-only {
        background-color: #fff;
        font-weight: 600;
        padding: 5px 3px;
        color: ${({ theme }) => theme.brandColors.CA_Black};
    }

    &.noBorder-input {
        border: 1px solid ${({ theme }) => theme.brandColors.CA_Light_Gray};
        border-width: 0 0px 1px 0px;
        background-color: white;
        font-weight: 400;
        & ~ label {
            color: ${({ theme }) => theme.brandColors.CA_Black};
        }
        &:read-only {
            border-width: 1px;
            border-color: ${({ theme }) => theme.brandColors.CA_Light_Gray};
            color: ${({ theme }) => theme.brandColors.CA_Mid_Gray};
            background-color: unset;
        }
    }

    &::-ms-reveal {
        display: none;
    }

    &.search-input {
        background-color: none;
        border: 1px solid ${({ theme }) => theme.input.borderColor};
        padding: 10px 15px;
        position: relative;
        @media (max-width: 600px) {
            width: 100%;
        }
    }

    &.checkbox-black {
        padding: 0;
        appearance: none;
        background-color: unset;
        border: none;
        position: relative;
        width: 18px;
        height: 18px;
        cursor: pointer;
        flex-shrink: 0;
    }

    &.rounded {
        border-radius: 24px;
    }
`

interface IInputProps extends ComponentPropsWithRef<'input'> {
    id: string
    labelText?: string
    infoText?: string
    error?: string
    subtitle?: ReactNode
    unit?: ReactNode
    readonly?: boolean
    wrapperClassName?: string
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    disabledReason?: string
    dropdownMenu?: React.ReactNode
    icon?: React.ReactNode
}

const Input = forwardRef<HTMLInputElement, IInputProps>(({ onClick, ...props }: IInputProps, ref) => {
    const [showPassword, setShowPassword] = useState(false)

    return (
        <InputWrapper
            unit={props.unit}
            subtitle={props.subtitle}
            error={props.error}
            id={props.id}
            type={props.type}
            {...(props.dropdownMenu && { dropdownMenu: props.dropdownMenu })}
            {...(props.disabled && { disabled: props.disabled })}
            {...(props.disabledReason && { disabledReason: props.disabledReason })}
            {...(props.wrapperClassName && { className: props.wrapperClassName })}
            {...(props.labelText && { labelText: props.labelText })}
            {...(props.required && { required: props.required })}
            {...(props.infoText && { infoText: props.infoText })}
            onClick={onClick}
        >
            {props.icon && (
                <span className="flex absolute z-1" style={{ left: '1rem' }}>
                    {props.icon}
                </span>
            )}
            <SInput
                ref={ref}
                placeholder=" "
                autoComplete="off"
                {...props}
                className={cn(props.className, {
                    password: props.type === 'password',
                    'read-only': props.readonly,
                    'checkbox-black': props.type === 'checkbox',
                })}
                readOnly={props.readonly}
                required={props.required}
                type={props.type === 'password' && showPassword ? 'text' : props.type}
                {...(props.required && { 'aria-required': true })}
            />

            {props.type === 'password' ? (
                <SShowPasswordToggle
                    tabIndex={-1}
                    type="button"
                    data-testid="eyeIconBtn"
                    onClick={() => setShowPassword(!showPassword)}
                >
                    {showPassword ? <EyeIcon /> : <CloseEyeIcon />}
                </SShowPasswordToggle>
            ) : null}
        </InputWrapper>
    )
})

export default Input
